import { NumberFormatValues } from 'react-number-format';
import { useCallback } from 'react';
import {
  useRfqBuilderActions,
  useRfqBuilderCategorizedLegView,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';
import { roundedLegRatio } from '@paradigm/features/src/unified-rfqs/utils/legs';

import PopoverCell from '#/components/UnifiedRfqs/components/PopoverCell';
import { ReadonlyTransparentNumberField } from '@paradigm/design-system/src/components/Input';
import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';
import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';
import { BaseNumericField } from './BaseNumericField';

export default function RatioField() {
  const { index, ratio, isHedge } = useRfqBuilderCategorizedLegView();
  const { changeLegRatio } = useRfqBuilderActions();
  const { roundedRatio, isRounded } = roundedLegRatio(ratio.value);
  const { didSubmit } = useSubmitted();
  const { strategy, strategyShortcut } = useRfqBuilderView();
  const trackAction = useDebouncedTrackAction();

  const strategyCode =
    strategyShortcut.selectedOption?.value ??
    strategy.selectedOption?.value ??
    null;

  const handleOnChange = useCallback(
    ({ value }: NumberFormatValues) => {
      trackAction('update strategy leg', {
        strategyCode,
        ratio: value,
        isHedge,
      });
      if (value !== ratio.value) changeLegRatio(index, value);
    },
    [changeLegRatio, index, ratio.value, strategyCode, trackAction, isHedge],
  );

  if (ratio.isDisabled) {
    return (
      <PopoverCell
        id={`um-rfq-builder-leg-row-${index}-ratio`}
        shouldDisplayPopover={isRounded}
        popoverValue={ratio.value}
      >
        <ReadonlyTransparentNumberField
          size="small"
          aria-label="Ratio"
          value={roundedRatio}
        />
      </PopoverCell>
    );
  }

  return (
    <BaseNumericField
      aria-label="Ratio"
      placeholder="Enter"
      disabled={ratio.isDisabled}
      value={ratio.value}
      decimalScale={ratio.precision}
      onValueChange={handleOnChange}
      warning={!ratio.isValid && didSubmit}
    />
  );
}
